import { useMemo, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import { CARD_CLASS, DATA_GRID_SX, TABLE_ACCENTS } from "../styles";
import type { GridRow, MinPriceSourceData, TableAccent } from "../types";
import { formatPrice, getComparableValue, getPriceAlpha } from "../utils";

function DiffCell({ value, client, competitor }: { value: number | null; client: number | null; competitor: number | null }) {
  if (value === null) {
    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <div className="flex flex-col gap-1 text-xs">
            <span>Мой объект: {formatPrice(client)}</span>
            <span>Конкуренты: {formatPrice(competitor)}</span>
            <span>Разница: —</span>
          </div>
        }
      >
        <div className="flex items-center gap-3 text-slate-400">
          <span className="text-sm font-semibold">—</span>
          <div className="h-2 flex-1 rounded-full bg-slate-100" />
        </div>
      </Tooltip>
    );
  }

  const abs = Math.min(Math.abs(value), 60);
  const width = `${Math.max(12, Math.round((abs / 60) * 100))}%`;
  const toneClass = value > 0 ? "text-emerald-600" : value < 0 ? "text-rose-600" : "text-slate-500";
  const barClass = value > 0 ? "bg-emerald-500" : value < 0 ? "bg-rose-500" : "bg-slate-300";

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <div className="flex flex-col gap-1 text-xs">
          <span>Мой объект: {formatPrice(client)}</span>
          <span>Конкуренты: {formatPrice(competitor)}</span>
          <span>
            Разница: {value > 0 ? "+" : ""}
            {value.toFixed(1)}%
          </span>
        </div>
      }
    >
      <div className="flex w-full items-center justify-center gap-2">
        <span className={["text-[14px] font-semibold", toneClass].join(" ")}>
          {value > 0 ? "+" : ""}
          {value.toFixed(1)}%
        </span>
        <div className="relative h-2 w-20 rounded-full bg-slate-100">
          <div
            className={["absolute left-0 top-0 h-2 rounded-full", barClass].join(" ")}
            style={{ width }}
          />
        </div>
      </div>
    </Tooltip>
  );
}

function MinPriceInfoTooltip({ data }: { data?: MinPriceSourceData | null }) {
  const hotelName = data?.hotel_name ?? data?.hotelName ?? data?.hotel ?? "—";
  const roomName = data?.hotel_room ?? data?.room_name ?? data?.roomName ?? data?.room ?? "—";
  const category = data?.room_category ?? data?.category ?? "—";
  const platform = data?.platform ?? data?.ota ?? "—";

  return (
    <div className="flex flex-col gap-1 text-xs">
      <span>Отель: {hotelName}</span>
      <span>Номер: {roomName}</span>
      <span>Категория: {category}</span>
      <span>Платформа: {platform}</span>
    </div>
  );
}

const parseDateParts = (value: unknown) => {
  const text = String(value ?? "");
  const isoMatch = text.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return { day: Number(day), month: Number(month), year: Number(year) };
  }

  const ruMatch = text.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (ruMatch) {
    const [, day, month, year] = ruMatch;
    return { day: Number(day), month: Number(month), year: Number(year) };
  }

  return null;
};

const parseDate = (value: unknown) => {
  const parts = parseDateParts(value);
  if (!parts?.day || !parts.month || !parts.year) return 0;
  return Date.UTC(parts.year, parts.month - 1, parts.day);
};

const WEEKDAY_LABELS = ["ВС", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"];

const getWeekdayLabel = (value: unknown) => {
  const timestamp = parseDate(value);
  if (!timestamp) return "—";
  return WEEKDAY_LABELS[new Date(timestamp).getUTCDay()];
};

const applySort = (rows: GridRow[], sortModel: GridSortModel) => {
  if (!sortModel.length) return rows;
  const [{ field, sort }] = sortModel;
  if (!sort) return rows;

  return [...rows].sort((a, b) => {
    const aValue = field === "date" ? parseDate(a.date) : field === "weekday" ? parseDate(a.date) : getComparableValue(a, field);
    const bValue = field === "date" ? parseDate(b.date) : field === "weekday" ? parseDate(b.date) : getComparableValue(b, field);

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === "string" && typeof bValue === "string") {
      const result = aValue.localeCompare(bValue, "ru");
      return sort === "asc" ? result : -result;
    }

    const diff = Number(aValue) - Number(bValue);
    return sort === "asc" ? diff : -diff;
  });
};

const buildGridColumns = (
  minPrice: number,
  maxPrice: number,
  onPriceClick?: (row: GridRow) => void,
  showMinPriceInfo = false,
): GridColDef<GridRow>[] => [
  {
    field: "date",
    headerName: "Дата",
    minWidth: 88,
    flex: 0.55,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<GridRow, string>) => (
      <span className="text-sm font-medium text-slate-600">{params.value}</span>
    ),
  },
  {
    field: "weekday",
    headerName: "День недели",
    minWidth: 80,
    flex: 0.45,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<GridRow>) => (
      <span className="inline-flex min-w-10 items-center justify-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
        {getWeekdayLabel(params.row.date)}
      </span>
    ),
  },
  {
    field: "client",
    headerName: "Мой объект",
    minWidth: 108,
    flex: 0.95,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<GridRow, number | null>) => {
      const content = <span className="text-[15px] font-semibold text-slate-900">{formatPrice(params.value ?? null)}</span>;
      const clickableContent = onPriceClick ? (
        <button
          type="button"
          onClick={() => onPriceClick(params.row)}
          className="cursor-pointer transition hover:opacity-80"
        >
          {content}
        </button>
      ) : content;

      if (!showMinPriceInfo) return clickableContent;
      return (
        <Tooltip arrow placement="top" title={<MinPriceInfoTooltip data={params.row.clientMinData} />}>
          <span>{clickableContent}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "competitor",
    headerName: "Конкуренты",
    minWidth: 116,
    flex: 1.05,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<GridRow, number | null>) => {
      const content = (
        <span
          className="inline-flex min-w-[84px] items-center justify-center rounded-none px-3 py-1 text-[14px] font-semibold text-indigo-700"
          style={{ backgroundColor: `rgba(99, 102, 241, ${getPriceAlpha(params.value ?? null, minPrice, maxPrice)})` }}
        >
          {formatPrice(params.value ?? null)}
        </span>
      );
      const clickableContent = onPriceClick ? (
        <button
          type="button"
          onClick={() => onPriceClick(params.row)}
          className="cursor-pointer transition hover:opacity-80"
        >
          {content}
        </button>
      ) : content;

      if (!showMinPriceInfo) return clickableContent;
      return (
        <Tooltip arrow placement="top" title={<MinPriceInfoTooltip data={params.row.competitorMinData} />}>
          <span>{clickableContent}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "diffPercent",
    headerName: "Разница, %",
    minWidth: 156,
    flex: 1.3,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<GridRow, number | null>) => (
      <DiffCell value={params.value ?? null} client={params.row.client} competitor={params.row.competitor} />
    ),
  },
];

type Props = {
  title: string;
  rows: GridRow[];
  accent?: TableAccent;
  visibleCount: number;
  onPriceClick?: (row: GridRow) => void;
  showMinPriceInfo?: boolean;
};

export default function TableCard({ title, rows, accent = "violet", visibleCount, onPriceClick, showMinPriceInfo = false }: Props) {
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "date", sort: "asc" }]);
  const sortedRows = useMemo(() => applySort(rows, sortModel), [rows, sortModel]);
  const accentStyle = TABLE_ACCENTS[accent];
  const { minPrice, maxPrice } = useMemo(() => {
    const values = rows.map((row) => row.competitor).filter((value): value is number => typeof value === "number");
    if (!values.length) return { minPrice: 0, maxPrice: 0 };
    return { minPrice: Math.min(...values), maxPrice: Math.max(...values) };
  }, [rows]);
  const columns = useMemo(
    () => buildGridColumns(minPrice, maxPrice, onPriceClick, showMinPriceInfo),
    [minPrice, maxPrice, onPriceClick, showMinPriceInfo],
  );
  const rowHeight = 56;
  const headerHeight = 42;
  const rowsToShow = Math.max(1, Math.min(visibleCount, rows.length || 1));
  const gridHeight = headerHeight + rowsToShow * rowHeight + 2;

  return (
    <section className={`${CARD_CLASS} min-w-0`}>
      <div className="rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={["h-8 w-1.5 rounded-full", accentStyle.line].join(" ")} />
            <div>
              <h2 className="text-base font-semibold text-slate-900">{title}</h2>
              <p className="text-xs text-slate-500">Сравнение цен Вашего объекта с конкурентами</p>
            </div>
          </div>
          <span
            className={[
              "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
              accentStyle.pill,
            ].join(" ")}
          >
            {rows.length} записей
          </span>
        </div>
      </div>

      <div className="mt-5">
        <DataGrid
          autoHeight={false}
          rows={sortedRows}
          columns={columns}
          rowSelection={false}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterSelectedRowCount
          hideFooter
          rowHeight={56}
          columnHeaderHeight={42}
          sx={DATA_GRID_SX}
          style={{ height: gridHeight }}
        />
      </div>
    </section>
  );
}
